<template>
  <v-form
    v-if="model"
    ref="form"
    @submit.prevent
  >
    <v-container
      v-if="user"
      class="brand-green-dark--text"
      fluid
      pa-0
    >
      <v-row dense>
        <v-col cols="12">
          <h3 class="brand-text-title">
            {{ toLocaleUpperCase($t('Account.Settings.Profile')) }}
          </h3>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Firstname.Label') }}
        </v-col>

        <v-col class="auto">
          <v-text-field
            v-model="model.Firstname"
            :error-messages="serverErrorMessages['first_name']"
            :hint="$t('Account.Settings.Field.Firstname.Hint')"
            :placeholder="$t('Account.Settings.Field.Firstname.Placeholder')"
            :rules="model.validator.vuetifyFormFieldRules('Firstname')"
            clearable
            dense
            flat
            name="Firstname"
            outlined
            prepend-inner-icon="mdi-account-circle"
            type="text"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Lastname.Label') }}
        </v-col>

        <v-col class="auto">
          <v-text-field
            v-model="model.Lastname"
            :error-messages="serverErrorMessages['last_name']"
            :hint="$t('Account.Settings.Field.Lastname.Hint')"
            :placeholder="$t('Account.Settings.Field.Lastname.Placeholder')"
            :rules="model.validator.vuetifyFormFieldRules('Lastname')"
            clearable
            dense
            flat
            name="Lastname"
            outlined
            prepend-inner-icon="mdi-account-circle"
            type="text"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col class="pb-8">
          {{ $t('Account.Settings.Field.Phone.Label') }}
        </v-col>

        <v-col class="auto">
          <div class="text-no-wrap">
            <vue-tel-input-vuetify
              :error-messages="serverErrorMessages['phone']"
              :hint="$t('Account.Settings.Field.Phone.Hint')"
              :init-country-value="getRegionCodeForCountryCode(model?.PhonePrefix || '30')"
              :init-phone-value="clone(model?.Phone || '')"
              :placeholder="$t('Account.Settings.Field.Phone.Placeholder')"
              :preferred-countries="['gr', 'cy']"
              :rules="model.validator.vuetifyFormFieldRules('Phone')"
              :valid-characters-only="true"
              clearable
              dense
              mode="significant"
              name="phone"
              outlined
              prepend-inner-icon="mdi-cellphone-text"
              text-field-classes="ml-2"
              @input="(formattedNumber, phone) => {
                model.Phone = phone.number.significant
                model.PhonePrefix = phone.countryCode
                validateForm()
              }"
            >
              <template #append>
                <v-btn
                  v-if="phoneNeedsOtpValidation"
                  class="px-2"
                  color="error"
                  depressed
                  height="24"
                  small
                  @click="onSaveClick"
                >
                  VALIDATE
                </v-btn>
              </template>
            </vue-tel-input-vuetify>
          </div>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Email.Label') }}
        </v-col>

        <v-col class="auto">
          <v-text-field
            v-model="model.Email"
            :disabled="true"
            :error-messages="serverErrorMessages['email']"
            :hint="$t('Account.Settings.Field.Email.Hint')"
            :placeholder="$t('Account.Settings.Field.Email.Placeholder')"
            :rules="model.validator.vuetifyFormFieldRules('Email')"
            clearable
            dense
            flat
            name="email"
            outlined
            prepend-inner-icon="mdi-email"
            type="email"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <h3 class="brand-text-title">
            {{ toLocaleUpperCase($t('Account.Settings.ChangePass')) }}
          </h3>
        </v-col>
      </v-row>

      <v-row
        class="align-start"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Password.Label') }}
        </v-col>

        <v-col class="auto">
          <v-text-field
            v-model="model.NewPass"
            :error-messages="serverErrorMessages['password']"
            :hint="$t('Account.Settings.Field.NewPass.Hint')"
            :placeholder="$t('Account.Settings.Field.NewPass.Placeholder')"
            :rules="model.validator.vuetifyFormFieldRules('NewPass')"
            clearable
            dense
            flat
            name="new-password"
            outlined
            prepend-inner-icon="mdi-lock"
            type="password"
          />

          <v-text-field
            v-model="model.NewPassConfirm"
            :error-messages="serverErrorMessages['password_confirmation']"
            :hint="$t('Account.Settings.Field.NewPassConfirm.Hint')"
            :placeholder="$t('Account.Settings.Field.NewPassConfirm.Placeholder')"
            :rules="model.validator.vuetifyFormFieldRules('NewPassConfirm')"
            clearable
            dense
            flat
            name="new-password-confirm"
            outlined
            prepend-inner-icon="mdi-lock"
            type="password"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <h3 class="brand-text-title">
            {{ toLocaleUpperCase($t('Account.Settings.Notification.Marketing')) }}
          </h3>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Email.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="model.Newsletter"
            :label="model.Newsletter ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('Newsletter')"
            class="pt-0 d-inline-flex"
            hide-details
            inset
            name="Newsletter"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Sms.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="model.SMS"
            :label="model.SMS ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('SMS')"
            class="pt-0 d-inline-flex"
            hide-details
            inset
            name="sms"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Push.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="pushMarketing"
            :disabled="pushNotificationsPermission === 'denied'"
            :error-messages="pushNotificationsPermission === 'denied' ? $t('Account.Settings.Field.Push.PermissionError') : ''"
            :label="pushMarketing ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('Push')"
            class="pt-0 d-inline-flex"
            hide-details="auto"
            inset
            name="push-notifications"
            @change="onPushChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col
          class="pt-6"
          cols="12"
        />
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <h3 class="brand-text-title">
            {{ toLocaleUpperCase($t('Account.Settings.Notification.System')) }}
          </h3>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Email.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="model.NewsletterSystem"
            :label="model.NewsletterSystem ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('NewsletterSystem')"
            class="pt-0 d-inline-flex"
            hide-details
            inset
            name="NewsletterSystem"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Sms.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="model.SMSSystem"
            :label="model.SMSSystem ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('SMSSystem')"
            class="pt-0 d-inline-flex"
            hide-details
            inset
            name="SMSSystem"
          />
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.Field.Push.Label') }}
        </v-col>

        <v-col class="auto">
          <v-switch
            v-model="pushSystem"
            :disabled="pushNotificationsPermission === 'denied'"
            :error-messages="pushNotificationsPermission === 'denied' ? $t('Account.Settings.Field.Push.PermissionError') : ''"
            :label="pushSystem ? $t('Common.Button.Yes') : $t('Common.Button.No')"
            :rules="model.validator.vuetifyFormFieldRules('PushSystem')"
            class="pt-0 d-inline-flex"
            hide-details="auto"
            inset
            name="PushSystem"
            @change="onPushChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col
          class="pt-6"
          cols="12"
        />
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <h3 class="brand-text-title">
            {{ toLocaleUpperCase($t('Account.Settings.Account')) }}
          </h3>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.ExportAccount') }}
        </v-col>

        <v-col class="auto">
          <v-btn
            block
            class="brand-text-title brand-green-dark--text"
            color="warning"
            depressed
            @click="onExportClick"
          >
            {{ toLocaleUpperCase($t('Common.Button.Export')) }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row
        class="align-center"
        dense
      >
        <v-col>
          {{ $t('Account.Settings.DeleteAccount') }}
        </v-col>

        <v-col class="auto">
          <v-btn
            block
            class="brand-text-title brand-green-dark--text"
            color="red"
            depressed
            @click="onDeleteClick"
          >
            {{ toLocaleUpperCase($t('Common.Button.Delete')) }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-btn
            :disabled="isFormSaving"
            :loading="isFormSaving"
            block
            class="brand-green-dark--text"
            color="brand-primary"
            depressed
            x-large
            @click="onSaveClick"
          >
            <span class="brand-text-title">{{ toLocaleUpperCase($t('Common.Button.Save')) }}</span>
          </v-btn>
        </v-col>
      </v-row>

      <confirm-pin-dialog
        :message="confirmDialog.content"
        :options="confirmDialog"
        :sub-title="confirmDialog.subTitle"
        :title="confirmDialog.title"
        :visible.sync="confirmDialog.visible"
        @dialog:yes="confirmDialog.callback"
      />

      <otp-dialog
        v-if="otpModel"
        v-model="otpModel"
        :otp-error="otpError"
        :otp-progress="otpProgress"
        :visible.sync="otpDialogVisible"
        @validate="otpValidate"
      />
    </v-container>
  </v-form>
</template>

<script>
import AccountSettingsMixin from '@/theme/default/views/account/AccountSettings.js'
import API from '@/app-core/api/Api.js'

export default {
  name   : 'AccountSettings',
  mixins : [AccountSettingsMixin],
  methods: {
    onDeleteClick() {
      this.confirmDialog.action = 'DELETE'

      this.$root.$confirm.open(
        this.$t('Account.Settings.Notification.Delete.Title'),
        this.$t('Account.Settings.Notification.Delete.Subtitle'),
        this.$t('Account.Settings.Notification.Delete.Body'),
        {
          color        : 'brand-primary',
          icon         : 'mdi-alert',
          coloredBorder: false,
          dark         : false,
          button       : {
            no : {title: this.$t('Common.Button.Cancel')},
            yes: {title: this.$t('Common.Button.Ok')}
          }
        }
      ).then(response => {
        if (response) this.onConfirmGetPin()
      })
    },
    onExportClick() {
      this.confirmDialog.action = 'EXPORT'

      this.$root.$confirm.open(
        this.$t('Account.Settings.Notification.Export.Title'),
        this.$t('Account.Settings.Notification.Export.Subtitle'),
        this.$t('Account.Settings.Notification.Export.Body'),
        {
          color        : 'brand-primary',
          icon         : 'mdi-alert',
          coloredBorder: false,
          dark         : false,
          button       : {
            no : {title: this.$t('Common.Button.Cancel')},
            yes: {title: this.$t('Common.Button.Ok')}
          }
        }
      ).then(response => {
        if (response) this.onConfirmGetPin()
      })
    },

    onConfirmGetPin() {
      this.model.ConfirmPin({CustomerId: this.model.Id})
        .then(response => {
          const data = API.responseData(response)
          if (API.isResponseSuccess(response)) {
            if (data.Messages.Success) {
              if (this.confirmDialog.action === 'EXPORT') {
                this.confirmDialog = {
                  visible      : true,
                  color        : 'brand-primary',
                  icon         : 'mdi-alert',
                  coloredBorder: false,
                  dark         : false,
                  input        : this.$t('Account.Settings.Notification.Export.ConfirmPin.Title'),
                  button       : {
                    no : {title: this.$t('Common.Button.Cancel')},
                    yes: {title: this.$t('Common.Button.Confirm')}
                  },
                  title   : this.$t('Account.Settings.Notification.Export.ConfirmPin.Title'),
                  subTitle: this.$t('Account.Settings.Notification.Export.ConfirmPin.Subtitle'),
                  content : this.$t('Account.Settings.Notification.Export.ConfirmPin.Body'),
                  callback: this.onConfirmExport
                }
              } else if (this.confirmDialog.action === 'DELETE') {
                this.confirmDialog = {
                  visible      : true,
                  color        : 'brand-primary',
                  icon         : 'mdi-alert',
                  coloredBorder: false,
                  dark         : false,
                  input        : this.$t('Account.Settings.Notification.Export.Delete.Title'),
                  button       : {
                    no : {title: this.$t('Common.Button.Cancel')},
                    yes: {title: this.$t('Common.Button.Confirm')}
                  },
                  title   : this.$t('Account.Settings.Notification.Export.Delete.Title'),
                  subTitle: this.$t('Account.Settings.Notification.Export.Delete.Subtitle'),
                  content : this.$t('Account.Settings.Notification.Export.Delete.Body'),
                  callback: this.onConfirmDelete
                }
              }
            } else if (data.Messages.Error) {
              this.$bus.$emit('app:show-notification', {
                title: '',
                body : data.Messages.Error,
                type : 'error',
                icon : ''
              })
            }
          }
        })
        .catch(e => {

        })
    }


  }
}
</script>

<style scoped src="@/theme/default/views/account/AccountSettings.css"></style>
<style scoped>
</style>
